// Page /app/parametres/equipe — gestion de l'équipe (Lot 15).
// Liste + invite + suspend/reactivate/archive + transfer ownership.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  UserPlus, ChevronLeft, Loader2, X, Mail, Shield, ShieldCheck, Ban,
  CheckCircle2, Archive, RefreshCw, Crown, MoreHorizontal, Info, Copy,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  active:    { label: "Actif",       bg: "bg-[#D9FFE8]",  fg: "text-[#166534]", dot: "bg-[#22C55E]" },
  invited:   { label: "Invité",       bg: "bg-[#DBEAFE]",  fg: "text-[#1E40AF]", dot: "bg-[#3B82F6]" },
  suspended: { label: "Suspendu",     bg: "bg-[#FEF3C7]",  fg: "text-[#92400E]", dot: "bg-[#F59E0B]" },
  archived:  { label: "Archivé",      bg: "bg-[#F1F5F9]",  fg: "text-[#475569]", dot: "bg-[#94A3B8]" },
};

const Pill = ({ status }) => {
  const c = STATUS_META[status] || STATUS_META.active;
  return (
    <span data-testid={`user-status-${status}`}
          className={`inline-flex items-center gap-1.5 rounded-full ${c.bg} ${c.fg} px-2.5 py-1 text-[11px] font-semibold`}>
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
};

const TeamPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // {activation_url}
  const [includeArchived, setIncludeArchived] = useState(false);
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [meRes, list, rolesRes] = await Promise.all([
        api.getMe(),
        api.listTeam(includeArchived),
        api.listRoles(),
      ]);
      setMe(meRes);
      setUsers(list.users || []);
      setRoles(rolesRes.roles || []);
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [includeArchived]);

  const canManage = me?.role === "agency_owner" || me?.role === "admin";

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10" data-testid="page-team">
      <Link to="/app/parametres"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6B7280] hover:text-[#111]">
        <ChevronLeft size={14} /> Retour aux paramètres
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-[-0.02em] text-[#111]">Équipe</h1>
          <p className="mt-1.5 text-[14px] text-[#6B7280]">
            Invitez et gérez les collaborateurs de votre agence. Les noms saisis ici
            servent à l'assignation des dossiers, livraisons et étapes de workflow.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowInvite(true)}
            data-testid="team-invite-btn"
            className="h-10 inline-flex items-center gap-2 rounded-full bg-[#111] text-white text-[13px] font-medium px-5 hover:bg-black transition-colors">
            <UserPlus size={15} strokeWidth={2.25} />
            Inviter un collaborateur
          </button>
        )}
      </div>

      <div className="mt-8 flex items-center gap-3">
        <button
          onClick={() => setIncludeArchived(!includeArchived)}
          data-testid="team-toggle-archived"
          className={`h-8 inline-flex items-center gap-1.5 rounded-full text-[12px] font-medium px-3 border transition
            ${includeArchived ? "border-[#111] bg-[#111] text-white" : "border-black/10 bg-white text-[#6B7280] hover:bg-[#F7F8FA]"}`}>
          {includeArchived ? "✓ " : ""}Inclure les archivés
        </button>
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-[#6B7280]">
          <Loader2 size={16} className="animate-spin" /> Chargement…
        </div>
      ) : error ? (
        <div className="mt-10 rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3">{error}</div>
      ) : (
        <div className="mt-6 space-y-2" data-testid="team-list">
          {users.map((u) => (
            <UserRow
              key={u.id}
              u={u}
              me={me}
              roles={roles}
              onChange={load}
              canManage={canManage}
            />
          ))}
        </div>
      )}

      {showInvite && (
        <InviteModal
          roles={roles}
          onClose={() => setShowInvite(false)}
          onInvited={async (payload) => {
            setInviteBusy(true);
            try {
              const res = await api.inviteTeamMember(payload);
              setLastInvite(res);
              setShowInvite(false);
              await load();
            } catch (e) {
              throw e;
            } finally {
              setInviteBusy(false);
            }
          }}
          busy={inviteBusy}
        />
      )}
      {lastInvite && (
        <InvitationSentModal
          data={lastInvite}
          onClose={() => setLastInvite(null)}
        />
      )}
    </div>
  );
};

// ---------- Row ----------

const UserRow = ({ u, me, roles, onChange, canManage }) => {
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const isSelf = me?.id === u.id;
  const isOwner = u.role === "agency_owner";

  const initials = (u.first_name?.[0] || "") + (u.last_name?.[0] || "");

  const roleLabel = roles.find((r) => r.role === u.role)?.label || u.role;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await onChange(); } finally { setBusy(false); setMenuOpen(false); }
  };

  return (
    <div
      data-testid={`team-row-${u.id}`}
      className="rounded-2xl border border-black/5 bg-white px-5 py-4 flex items-center gap-4 hover:border-black/10 transition-colors">
      <div className="h-10 w-10 rounded-full bg-[#F7F8FA] text-[#111] flex items-center justify-center text-[13px] font-semibold shrink-0">
        {(initials || u.email?.[0] || "?").toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[14.5px] font-semibold text-[#111] truncate">{u.name}</p>
          {isOwner && <Crown size={13} className="text-[#8FB600] shrink-0" />}
          {isSelf && (
            <span className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">Vous</span>
          )}
        </div>
        <p className="text-[12.5px] text-[#6B7280] truncate">{u.email}</p>
      </div>
      <div className="hidden sm:flex flex-col items-end shrink-0">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">{roleLabel}</span>
        {u.phone && <span className="text-[11px] text-[#6B7280] mt-0.5">{u.phone}</span>}
      </div>
      <div className="shrink-0"><Pill status={u.status} /></div>

      {canManage && !isSelf && (
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid={`team-row-actions-${u.id}`}
            disabled={busy}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F7F8FA] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <MoreHorizontal size={16} />}
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 z-10 w-56 rounded-2xl bg-white border border-black/5 shadow-xl py-2"
                 onMouseLeave={() => setMenuOpen(false)}>
              {u.status === "invited" && (
                <MenuItem icon={<RefreshCw size={13} />} label="Renvoyer l'invitation"
                          onClick={() => act(async () => {
                            const res = await api.resendInvitation(u.id);
                            toast.success("Nouveau lien d\x27activation généré", { description: res.activation_url });
                          })} testId={`menu-resend-${u.id}`} />
              )}
              {u.status === "active" && !isOwner && (
                <MenuItem icon={<Ban size={13} />} label="Suspendre l'accès"
                          onClick={() => act(() => api.setTeamMemberStatus(u.id, "suspended"))}
                          testId={`menu-suspend-${u.id}`} />
              )}
              {u.status === "suspended" && (
                <MenuItem icon={<CheckCircle2 size={13} />} label="Réactiver"
                          onClick={() => act(() => api.setTeamMemberStatus(u.id, "active"))}
                          testId={`menu-reactivate-${u.id}`} />
              )}
              {!isOwner && u.status !== "archived" && (
                <>
                  <div className="my-1 h-px bg-black/5" />
                  <MenuItem icon={<Archive size={13} />} label="Archiver"
                            onClick={() => act(() => api.setTeamMemberStatus(u.id, "archived",
                                              "Retrait de l'équipe"))}
                            danger testId={`menu-archive-${u.id}`} />
                </>
              )}
              {me?.role === "agency_owner" && u.status === "active" && !isOwner && (
                <>
                  <div className="my-1 h-px bg-black/5" />
                  <MenuItem icon={<Crown size={13} />} label="Transférer la propriété"
                            onClick={() => { setTransferOpen(true); setMenuOpen(false); }}
                            testId={`menu-transfer-${u.id}`} />
                </>
              )}
            </div>
          )}
        </div>
      )}
      {transferOpen && (
        <TransferOwnershipModal
          user={u}
          onClose={() => setTransferOpen(false)}
          onConfirmed={async () => {
            await api.transferOwnership(u.id);
            setTransferOpen(false);
            await onChange();
          }}
        />
      )}
    </div>
  );
};

const MenuItem = ({ icon, label, onClick, danger, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left px-4 py-2 text-[13px] font-medium flex items-center gap-2.5 hover:bg-[#F7F8FA] ${danger ? "text-[#B91C1C]" : "text-[#111]"}`}>
    {icon}{label}
  </button>
);

// ---------- Modals ----------

const InviteModal = ({ roles, onClose, onInvited, busy }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("salesperson");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  const inviteableRoles = useMemo(
    () => (roles || []).filter((r) => r.role !== "agency_owner"),
    [roles]
  );

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await onInvited({
        first_name: firstName, last_name: lastName,
        email, role, phone: phone || null,
      });
    } catch (err) {
      setError(err.message || "Erreur");
    }
  };
  return (
    <ModalShell title="Inviter un collaborateur" onClose={onClose} testId="team-invite-modal">
      <p className="mt-1 text-[13px] text-[#6B7280] leading-relaxed">
        Un email d'invitation contenant un lien magique valable 7 jours
        sera envoyé au collaborateur pour finaliser son inscription.
      </p>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom" testId="invite-firstname">
            <input required value={firstName} onChange={(e) => setFirstName(e.target.value)}
              data-testid="invite-firstname-input"
              className="input" />
          </Field>
          <Field label="Nom" testId="invite-lastname">
            <input value={lastName} onChange={(e) => setLastName(e.target.value)}
              data-testid="invite-lastname-input"
              className="input" />
          </Field>
        </div>
        <Field label="Email professionnel" testId="invite-email">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="invite-email-input"
            className="input" />
        </Field>
        <Field label="Téléphone (facultatif)" testId="invite-phone">
          <input value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="06 20 88 27 01"
            data-testid="invite-phone-input"
            className="input" />
        </Field>
        <Field label="Rôle" testId="invite-role">
          <select value={role} onChange={(e) => setRole(e.target.value)}
            data-testid="invite-role-input"
            className="input bg-white">
            {inviteableRoles.map((r) => (
              <option key={r.role} value={r.role}>{r.label}</option>
            ))}
          </select>
        </Field>
        {error && (
          <div className="rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12.5px] px-3 py-2">{error}</div>
        )}
        <button type="submit" disabled={busy}
          data-testid="invite-submit"
          className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full bg-[#111] text-white text-[14px] font-medium hover:bg-black transition disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
          Envoyer l'invitation
        </button>
      </form>
    </ModalShell>
  );
};

const InvitationSentModal = ({ data, onClose }) => {
  const url = data?.activation_url;
  const copy = () => { navigator.clipboard?.writeText(url); };
  return (
    <ModalShell title="Invitation envoyée" onClose={onClose} testId="team-invite-sent-modal">
      <p className="mt-1 text-[13px] text-[#6B7280]">
        L'email est en cours de livraison. Vous pouvez aussi partager le lien d'activation directement :
      </p>
      <div className="mt-4 rounded-xl bg-[#F7F8FA] border border-black/5 p-3 flex items-center gap-2">
        <code data-testid="invite-sent-url"
              className="flex-1 text-[12px] font-mono text-[#111] break-all">{url}</code>
        <button onClick={copy}
                data-testid="invite-copy-url"
                className="shrink-0 h-8 w-8 rounded-full bg-white border border-black/10 flex items-center justify-center hover:bg-[#F7F8FA]">
          <Copy size={14} />
        </button>
      </div>
      <button onClick={onClose}
              className="mt-5 w-full h-11 rounded-full bg-[#111] text-white text-[14px] font-medium">
        Terminé
      </button>
    </ModalShell>
  );
};

const TransferOwnershipModal = ({ user, onClose, onConfirmed }) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const go = async () => {
    setBusy(true);
    try { await onConfirmed(); } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Transférer la propriété" onClose={onClose} testId="transfer-ownership-modal">
      <div className="mt-3 rounded-2xl bg-[#FEF3C7] border border-[#FCD34D] p-4 flex items-start gap-3">
        <Info size={16} className="text-[#92400E] mt-0.5 shrink-0" />
        <p className="text-[13px] text-[#92400E] leading-relaxed">
          Vous allez transférer le rôle de <b>Responsable d'agence</b> à
          <b> {user.name}</b>. Vous serez rétrogradé au rôle <b>Administrateur</b>.
          Cette action est irréversible.
        </p>
      </div>
      <p className="mt-4 text-[12.5px] text-[#6B7280]">
        Tapez <b>TRANSFERER</b> pour confirmer.
      </p>
      <input
        value={confirm} onChange={(e) => setConfirm(e.target.value)}
        data-testid="transfer-confirm-input"
        className="input mt-2" />
      <button
        disabled={busy || confirm !== "TRANSFERER"}
        onClick={go}
        data-testid="transfer-confirm-submit"
        className="mt-5 w-full h-11 rounded-full bg-[#B91C1C] text-white text-[14px] font-medium hover:bg-[#991B1B] transition disabled:opacity-50">
        {busy ? "…" : "Transférer maintenant"}
      </button>
    </ModalShell>
  );
};

// ---------- Utils ----------

const ModalShell = ({ title, onClose, children, testId }) => (
  <div
    data-testid={testId}
    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={onClose}>
    <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl relative"
         onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F7F8FA]"
              aria-label="Fermer">
        <X size={16} />
      </button>
      <h3 className="text-[17px] font-semibold text-[#111]">{title}</h3>
      {children}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[12px] font-semibold text-[#111]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

// Réutilisable via une classe utility — Tailwind ne l'aime pas nativement,
// on utilise juste className courante partout.
if (typeof document !== "undefined") {
  const s = document.getElementById("wyvio-team-input-style");
  if (!s) {
    const el = document.createElement("style");
    el.id = "wyvio-team-input-style";
    el.textContent = `.input { display:block; width:100%; height:44px; padding:0 16px; border-radius:12px; border:1px solid rgba(0,0,0,0.1); font-size:14px; outline:none; transition:border-color 0.15s; }
    .input:focus { border-color:#111; }
    textarea.input { height:auto; padding:12px 16px; }`;
    document.head.appendChild(el);
  }
}

export default TeamPage;
